import { useEffect, useRef, useState } from "react";

const MIRE_MEDIA_LOGO = "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-28/prod_agent_29681c11-d6f4-4494-92b6-9967ba534da1/mire_media_logo_b80c218352bb4f83b44633aa500aaeaa.png";
const MIRE_DRAMA_LOGO = "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-28/prod_agent_29681c11-d6f4-4494-92b6-9967ba534da1/mire_drama_logo_0a9f9404afd847c4b25252ced73e99f1.png";

/* ─── Particle canvas ─── */
function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animId: number;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    interface P { x: number; y: number; vx: number; vy: number; r: number; a: number; }
    const N = 90;
    const pts: P[] = Array.from({ length: N }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.42, vy: (Math.random() - 0.5) * 0.42,
      r: Math.random() * 1.8 + 0.4, a: Math.random() * 0.5 + 0.15,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < N; i++) {
        const p = pts[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width; if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height; if (p.y > canvas.height) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,28,53,${p.a})`; ctx.fill();
        for (let j = i + 1; j < N; j++) {
          const q = pts[j];
          const dx = p.x - q.x, dy = p.y - q.y, d = Math.sqrt(dx * dx + dy * dy);
          if (d < 130) {
            ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(255,28,53,${(1 - d / 130) * 0.15})`; ctx.lineWidth = 0.6; ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }} />;
}

/* ─── Glitch ─── */
function Glitch({ text }: { text: string }) {
  return <span className="glitch" data-text={text}>{text}</span>;
}

/* ─── Contact row ─── */
function CRow({ icon, label, value, href }: { icon: React.ReactNode; label: string; value: string; href?: string }) {
  const [ok, setOk] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(value).then(() => { setOk(true); setTimeout(() => setOk(false), 1800); });
  };
  return (
    <div className="crow">
      <span className="cicon">{icon}</span>
      <div className="cinfo">
        <span className="clabel">{label}</span>
        {href ? <a href={href} className="cval" target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{value}</a>
          : <span className="cval">{value}</span>}
      </div>
      <button onClick={copy} className="ccopy" title="Copy">
        {ok
          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#FF1C35" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>}
      </button>
    </div>
  );
}

const IEmail = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>;
const IPhone = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.58 3.4 2 2 0 0 1 3.55 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.18 6.18l.87-.87a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>;
const IPin  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
const IGlobe= () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;

const QR = [1,1,1,1,1, 1,0,1,0,1, 1,1,0,1,1, 1,0,0,0,1, 1,1,1,1,1, 0,1,0,1,0, 1,0,1,0,1];

export default function Index() {
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), 100); return () => clearTimeout(t); }, []);

  const saveVcard = () => {
    const vc = ["BEGIN:VCARD","VERSION:3.0","FN:Heathen (万江明)","N:万江明;Heathen;;;","ORG:MIRE Group;MIRE Media","TITLE:CEO & Founder","EMAIL:wjm@miregroup.ai","TEL;TYPE=CELL:+601128435132","TEL;TYPE=WORK:+8615773113415","ADR;TYPE=WORK:;;1802 VIDA BUKIT CEYLON\\, JALAN CEYLON;KUALA LUMPUR;;;MALAYSIA","URL:https://miregroup.ai","END:VCARD"].join("\n");
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([vc], { type: "text/vcard" })), download: "Heathen_MIRE.vcf" });
    a.click();
  };
  const share = () => {
    if (navigator.share) navigator.share({ title: "Heathen | MIRE Group", text: "CEO & Founder · wjm@miregroup.ai", url: location.href });
    else navigator.clipboard.writeText(location.href);
  };

  return (
    <>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        html,body{height:100%;background:#08080d;overflow-x:hidden}

        .bg{
          position:fixed;inset:0;z-index:0;
          background:
            radial-gradient(ellipse 80% 55% at 50% -8%,rgba(255,28,53,.2) 0%,transparent 68%),
            radial-gradient(ellipse 45% 35% at 85% 105%,rgba(255,28,53,.12) 0%,transparent 55%),
            radial-gradient(ellipse 50% 40% at -5% 65%,rgba(120,0,18,.12) 0%,transparent 55%),
            linear-gradient(155deg,#0e080f 0%,#07070f 45%,#0a0408 100%);
        }
        .bg::after{content:'';position:absolute;inset:0;pointer-events:none;
          background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,.055) 2px,rgba(0,0,0,.055) 4px)}

        .page{position:relative;z-index:1;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:28px 16px}

        /* ── Card ── */
        .card{
          width:100%;max-width:480px;border-radius:26px;
          border:1px solid rgba(255,28,53,.28);
          background:rgba(12,8,14,.78);
          backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);
          box-shadow:0 0 0 1px rgba(255,28,53,.07) inset,0 36px 90px -22px rgba(0,0,0,.92),0 0 70px -12px rgba(255,28,53,.13);
          padding:36px 32px 30px;position:relative;overflow:hidden;
          opacity:0;transform:translateY(32px) scale(.97);
          transition:opacity .75s cubic-bezier(.22,1,.36,1),transform .75s cubic-bezier(.22,1,.36,1);
        }
        .card.show{opacity:1;transform:translateY(0) scale(1)}
        .card::before{content:'';position:absolute;top:-90px;left:50%;transform:translateX(-50%);width:340px;height:190px;pointer-events:none;z-index:0;background:radial-gradient(ellipse,rgba(255,28,53,.24) 0%,transparent 70%)}
        .card::after{content:'';position:absolute;top:0;left:8%;right:8%;height:1.5px;background:linear-gradient(90deg,transparent,rgba(255,28,53,.88) 28%,#FF1C35 50%,rgba(255,28,53,.88) 72%,transparent);border-radius:2px}

        /* ── Brand header ── */
        .hd{position:relative;z-index:1;display:flex;align-items:center;gap:10px;margin-bottom:22px;flex-wrap:wrap}
        .hd-logos{display:flex;align-items:center;gap:10px;flex:1}
        .logo-badge{
          height:38px;border-radius:9px;overflow:hidden;
          border:1px solid rgba(255,28,53,.22);
          background:rgba(0,0,0,.6);
          display:flex;align-items:center;justify-content:center;padding:0 8px;
          transition:border-color .2s,box-shadow .2s;
        }
        .logo-badge:hover{border-color:rgba(255,28,53,.5);box-shadow:0 0 14px rgba(255,28,53,.2)}
        .logo-badge img{height:26px;width:auto;object-fit:contain;display:block}
        .logo-badge.drama img{height:22px}
        .hd-sep{width:1px;height:28px;background:rgba(255,28,53,.25);flex-shrink:0}
        .hd-line{flex:1;height:1px;background:linear-gradient(90deg,rgba(255,28,53,.25),transparent);min-width:20px}

        /* ── Name ── */
        .name-sec{position:relative;z-index:1;margin-bottom:5px}
        .glitch{display:inline-block;position:relative;color:#fff}
        .glitch::before,.glitch::after{content:attr(data-text);position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none}
        .glitch::before{left:2px;text-shadow:-1px 0 #FF1C35;clip-path:polygon(0 18%,100% 18%,100% 38%,0 38%);animation:g1 5s infinite step-start}
        .glitch::after{left:-2px;text-shadow:1px 0 rgba(0,200,255,.55);clip-path:polygon(0 58%,100% 58%,100% 78%,0 78%);animation:g2 5s infinite step-start}
        @keyframes g1{0%,78%,100%{opacity:0;transform:none}79%{opacity:1;transform:translateX(-3px) skewX(-2deg)}81%{opacity:0}83%{opacity:1;transform:translateX(3px)}85%{opacity:0}}
        @keyframes g2{0%,83%,100%{opacity:0;transform:none}84%{opacity:1;transform:translateX(3px) skewX(3deg)}86%{opacity:0}89%{opacity:1;transform:translateX(-2px)}91%{opacity:0}}

        .name-en{font-family:'Orbitron',sans-serif;font-size:30px;font-weight:900;letter-spacing:4px;text-transform:uppercase;color:#fff;line-height:1.1;text-shadow:0 0 32px rgba(255,28,53,.32)}
        .name-cn{display:block;font-family:'Noto Sans SC',sans-serif;font-size:13px;font-weight:500;color:rgba(255,255,255,.42);letter-spacing:8px;margin-top:5px}

        /* ── Roles ── */
        .roles{position:relative;z-index:1;display:flex;flex-wrap:wrap;gap:7px;margin-top:13px;margin-bottom:24px}
        .role{display:inline-flex;align-items:center;gap:6px;padding:4px 11px;border-radius:7px;border:1px solid rgba(255,28,53,.3);background:rgba(255,28,53,.07);font-family:'Orbitron',sans-serif;font-size:9px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,190,190,.88)}
        .role-cn{font-family:'Noto Sans SC',sans-serif;font-size:10px;letter-spacing:1px}
        .dot{width:5px;height:5px;border-radius:50%;background:#FF1C35;box-shadow:0 0 7px rgba(255,28,53,.95);animation:pd 2s infinite;flex-shrink:0}
        .dot2{background:rgba(255,28,53,.45);box-shadow:none;animation:none}
        @keyframes pd{0%,100%{box-shadow:0 0 7px rgba(255,28,53,.9)}50%{box-shadow:0 0 14px rgba(255,28,53,1),0 0 22px rgba(255,28,53,.45)}}

        /* ── Divider ── */
        .div{position:relative;z-index:1;height:1px;margin-bottom:20px;background:linear-gradient(90deg,rgba(255,28,53,.38) 0%,rgba(255,28,53,.08) 65%,transparent 100%)}

        /* ── Contacts ── */
        .contacts{position:relative;z-index:1;display:flex;flex-direction:column;gap:8px}
        .crow{display:flex;align-items:center;gap:11px;padding:9px 12px;border-radius:10px;border:1px solid transparent;transition:border-color .22s,background .22s;cursor:default}
        .crow:hover{border-color:rgba(255,28,53,.22);background:rgba(255,28,53,.05)}
        .cicon{width:30px;height:30px;border-radius:8px;flex-shrink:0;background:rgba(255,28,53,.1);border:1px solid rgba(255,28,53,.2);display:flex;align-items:center;justify-content:center;color:#FF1C35}
        .cinfo{flex:1;min-width:0}
        .clabel{display:block;font-family:'Orbitron',sans-serif;font-size:7.5px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:rgba(255,28,53,.6);margin-bottom:2px}
        .cval{display:block;font-family:'JetBrains Mono',monospace;font-size:11.5px;font-weight:500;color:rgba(255,255,255,.82);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-decoration:none;transition:color .18s}
        a.cval:hover{color:#FF1C35}
        .ccopy{width:26px;height:26px;border-radius:6px;border:1px solid rgba(255,28,53,.14);background:transparent;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.28);cursor:pointer;opacity:0;flex-shrink:0;transition:color .18s,border-color .18s,background .18s}
        .crow:hover .ccopy{opacity:1}
        .ccopy:hover{color:#FF1C35;border-color:rgba(255,28,53,.44);background:rgba(255,28,53,.08)}

        /* ── WhatsApp button ── */
        .wa-btn{
          position:relative;z-index:1;
          display:flex;align-items:center;gap:10px;
          width:100%;padding:11px 16px;margin-top:8px;
          border-radius:12px;border:1px solid rgba(37,211,102,.35);
          background:rgba(37,211,102,.07);cursor:pointer;
          text-decoration:none;
          transition:border-color .22s,background .22s,box-shadow .22s,transform .15s;
        }
        .wa-btn:hover{
          border-color:rgba(37,211,102,.6);
          background:rgba(37,211,102,.13);
          box-shadow:0 0 20px rgba(37,211,102,.15);
          transform:translateY(-1px);
        }
        .wa-icon{
          width:34px;height:34px;border-radius:9px;flex-shrink:0;
          background:rgba(37,211,102,.15);border:1px solid rgba(37,211,102,.3);
          display:flex;align-items:center;justify-content:center;
        }
        .wa-text{flex:1;min-width:0}
        .wa-label{display:block;font-family:'Orbitron',sans-serif;font-size:7.5px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:rgba(37,211,102,.65);margin-bottom:2px}
        .wa-num{display:block;font-family:'JetBrains Mono',monospace;font-size:11.5px;font-weight:500;color:rgba(255,255,255,.82)}
        .wa-arrow{color:rgba(37,211,102,.55);flex-shrink:0;transition:transform .2s}
        .wa-btn:hover .wa-arrow{transform:translateX(3px)}

        /* ── Footer ── */
        .foot{position:relative;z-index:1;margin-top:22px;padding-top:16px;border-top:1px solid rgba(255,255,255,.05);display:flex;align-items:center;justify-content:space-between;gap:12px}
        .slogan{font-family:'Orbitron',sans-serif;font-size:7px;font-weight:400;letter-spacing:1.8px;text-transform:uppercase;color:rgba(255,255,255,.2);line-height:1.6}
        .slogan span{color:rgba(255,28,53,.55)}
        .qr{width:52px;height:52px;border-radius:9px;border:1px solid rgba(255,28,53,.2);background:rgba(255,28,53,.04);display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .qr-g{display:grid;grid-template-columns:repeat(5,7px);gap:2px}
        .qr-c{width:7px;height:7px;border-radius:1.5px}

        /* ── Actions ── */
        .acts{position:relative;z-index:1;margin-top:12px;display:flex;gap:9px}
        .btn-s{
          flex:1;padding:10px;border-radius:10px;border:none;cursor:pointer;
          background:linear-gradient(135deg,#FF1C35 0%,#bf0016 100%);
          font-family:'Orbitron',sans-serif;font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#fff;
          display:flex;align-items:center;justify-content:center;gap:7px;
          box-shadow:0 4px 22px rgba(255,28,53,.42),inset 0 1px 0 rgba(255,255,255,.1);
          transition:box-shadow .2s,transform .15s;
        }
        .btn-s:hover{box-shadow:0 6px 30px rgba(255,28,53,.65),inset 0 1px 0 rgba(255,255,255,.15);transform:translateY(-1px)}
        .btn-sh{width:40px;border-radius:10px;border:1px solid rgba(255,28,53,.28);background:rgba(255,28,53,.06);color:rgba(255,28,53,.78);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:border-color .2s,background .2s,transform .15s}
        .btn-sh:hover{border-color:rgba(255,28,53,.58);background:rgba(255,28,53,.13);transform:translateY(-1px)}

        .credit{margin-top:18px;font-family:'Orbitron',sans-serif;font-size:7px;font-weight:400;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.11);text-align:center}

        @media(max-width:420px){.card{padding:24px 16px 20px}.name-en{font-size:25px}.hd-logos{gap:7px}}
      `}</style>

      <div className="bg" />
      <ParticleCanvas />

      <main className="page">
        <div className={`card${vis ? " show" : ""}`}>

          {/* ── Header with logos ── */}
          <header className="hd">
            <div className="hd-logos">
              <div className="logo-badge">
                <img src={MIRE_MEDIA_LOGO} alt="MIRE MEDIA" />
              </div>
              <div className="hd-sep" />
              <div className="logo-badge drama">
                <img src={MIRE_DRAMA_LOGO} alt="MIRE DRAMA" />
              </div>
            </div>
            <div className="hd-line" />
          </header>

          {/* ── Name ── */}
          <section className="name-sec">
            <div className="name-en"><Glitch text="HEATHEN" /></div>
            <span className="name-cn">万 · 江 · 明</span>
          </section>

          {/* ── Roles ── */}
          <div className="roles">
            <span className="role"><span className="dot" />CEO &amp; Founder</span>
            <span className="role"><span className="dot dot2" /><span className="role-cn">首席执行官 · 创始人</span></span>
          </div>

          {/* ── Divider ── */}
          <div className="div" />

          {/* ── Contacts ── */}
          <div className="contacts">
            <CRow icon={<IEmail />} label="Email" value="wjm@miregroup.ai" href="mailto:wjm@miregroup.ai" />
            <CRow icon={<IPhone />} label="Phone" value="+60 11-2843 5132" href="tel:+601128435132" />
            <CRow icon={<IPin />}   label="Address" value="1802, VIDA BUKIT CEYLON, JALAN CEYLON, KL" />
            <CRow icon={<IGlobe />} label="Website" value="miregroup.ai" href="https://miregroup.ai" />
          </div>

          {/* ── WhatsApp ── */}
          <a className="wa-btn" href="https://wa.me/8615773113415" target="_blank" rel="noreferrer">
            <span className="wa-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L.057 23.535a.75.75 0 0 0 .916.948l5.85-1.533A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.712 9.712 0 0 1-4.98-1.367l-.358-.213-3.707.972.99-3.614-.235-.372A9.714 9.714 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z" />
              </svg>
            </span>
            <span className="wa-text">
              <span className="wa-label">WhatsApp</span>
              <span className="wa-num">+86 157-7311-3415</span>
            </span>
            <span className="wa-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </span>
          </a>

          {/* ── Footer ── */}
          <footer className="foot">
            <p className="slogan">Next Generation<br /><span>AI Entertainment</span> Platform</p>
            <div className="qr">
              <div className="qr-g">
                {QR.map((v, i) => <div key={i} className="qr-c" style={{ background: v ? "rgba(255,28,53,.72)" : "transparent" }} />)}
              </div>
            </div>
          </footer>

          {/* ── Actions ── */}
          <div className="acts">
            <button className="btn-s" onClick={saveVcard}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Save Contact
            </button>
            <button className="btn-sh" title="Share" onClick={share}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            </button>
          </div>

        </div>
        <p className="credit">© 2026 MIRE GROUP · ALL RIGHTS RESERVED</p>
      </main>
    </>
  );
}
